<template>
  <div id="template-set-properties" class="cpl-aside-content workspace">
    <div class="template-set-properties-content workspace hide-scrollbar">
      <div class="option-properties workspace">
        <template v-if="templateEdit.hasOwnProperty('options')">
          <div v-for="(section, indx) in sections"
            :key="indx"
            :class="['option-property', 'section-wrapper', `${section.id}`, {'enable-tags': enableTags}]">
            <!-- Logo -->
            <template v-if="section.id === 'logo'">
              <div class="title"> {{optionProperties[section.index].name}} </div>

              <div v-if="optionProperties[section.index].id === 'logo'" class="content logo">
                <div v-if="optionProperties[section.index].editable" class="editable">
                  <el-button @click="handleSelectLogoClick" class="select-media"> </el-button>
                </div>
              </div>
            </template>

            <!-- Background Color -->
            <template v-if="section.id === 'backgroundColor'">
              <div class="title"> {{optionProperties[section.index].name}} </div>

              <div class="content color">
                <div class="presentation">

                  <div class="cstm-color-picker-wrapper">
                    <el-color-picker
                      :model-value="colorPickersRgb.logoColor"
                      @change="handlePropertyChange(optionProperties[section.index].id)"
                      @active-change="handleColorSwatchChange($event, optionProperties[section.index].id, 'logoColor')"
                      color-format="rgb"
                      :predefine="predefineColors"
                      :popper-class="'custom-color-picker-dropdown cpicker-group-1-drpdwn logo-color-picker'"
                      class="color-item">
                    </el-color-picker>

                    <el-color-picker
                      :model-value="colorPickersRgb.backgroundColor"
                      @change="handlePropertyChange(optionProperties[section.index].id)"
                      @active-change="handleColorSwatchChange($event, optionProperties[section.index].id, 'backgroundColor')"
                      color-format="rgb"
                      :predefine="predefineColors"
                      :popper-class="'custom-color-picker-dropdown cpicker-group-1-drpdwn background-color-picker'"
                      class="color-item">
                    </el-color-picker>

                    <el-color-picker
                      :model-value="colorPickersRgb.foregroundColor"
                      @change="handlePropertyChange(optionProperties[section.index].id)"
                      @active-change="handleColorSwatchChange($event, optionProperties[section.index].id, 'foregroundColor')"
                      color-format="rgb"
                      :predefine="predefineColors"
                      :popper-class="'custom-color-picker-dropdown cpicker-group-1-drpdwn foreground-color-picker'"
                      class="color-item">
                    </el-color-picker>
                  </div>

                  <div v-if="!optionProperties[section.index].editable" @click="handlePropertyClick(optionProperties[section.index].id)" class="cover"></div>
                </div>
              </div>
            </template>

            <template v-if="section.id === 'textProperties'">
              <template v-for="textProp in section.children">
                <!-- Text Font -->
                <template v-if="textProp.id === 'font'">
                  <div class="title"> {{optionProperties[textProp.index].name}} </div>

                  <div class="content font">
                    <font-list :fontOptionsProp="templateEdit.options" @font-list-change="handleFontListChange($event)"></font-list>
                  </div>
                </template>

                <!-- Text Color -->
                <template v-if="textProp.id === 'textColor'">
                  <div class="content color">
                    <div class="presentation">
                      <div class="cstm-color-picker-wrapper">
                        <el-color-picker
                          :model-value="templateEdit.options.textColor"
                          @change="handlePropertyChange(optionProperties[textProp.index].id)"
                          @active-change="handleColorSwatchChange($event, optionProperties[textProp.index].id, 'textColor')"
                          color-format="rgb"
                          :predefine="predefineColors"
                          :popper-class="'custom-color-picker-dropdown cpicker-group-2-drpdwn text-color-picker'"
                          class="color-item">
                        </el-color-picker>

                        <el-color-picker
                          :model-value="templateEdit.options.subtextColor"
                          @change="handlePropertyChange(optionProperties[textProp.index].id)"
                          @active-change="handleColorSwatchChange($event, optionProperties[textProp.index].id, 'subtextColor')"
                          color-format="rgb"
                          :predefine="predefineColors"
                          :popper-class="'custom-color-picker-dropdown cpicker-group-2-drpdwn subtext-color-picker'"
                          class="color-item">
                        </el-color-picker>
                      </div>

                      <div v-if="!optionProperties[textProp.index].editable" @click="handlePropertyClick(optionProperties[textProp.index].id)" class="cover"></div>
                    </div>
                  </div>
                </template>
              </template>
            </template>

            <!-- Tags -->
            <template v-if="section.id === 'tags' && enableTags">
              <div class="flex justify-between">
                <div class="title"> {{optionProperties[section.index].name}} </div>
                <FwbTooltip theme="light">
                  <template #trigger>
                    <font-awesome-icon class="px-2" icon="fa-solid fa-question" size="md" />
                  </template>
                  <template #content>
                    <div class="w-[200px] p-2">
                      <span class="text-bold text-xs">{{ $t('label.importantToNote') }}</span>
                      <p class="text-xs text-[#00000080] pt-2">{{ $t('texts.defaultTagsNote') }}</p>
                    </div>
                  </template>
                </FwbTooltip>
              </div>

              <div class="content tags">
                <template v-if="optionProperties[section.index].editable">
                  <tag-menu-item :tagsSummaryProp="tagsSummary"
                    :selectedTagsFromSliderProp="selectedTagsFromSlider"
                    :tagsProp="tagOptions"
                    :accessorComponentProp="'templateSets'"
                    @tags-change="handleTagMenuItemChange($event)">
                  </tag-menu-item>
                </template>
              </div>
            </template>

            <!-- Name -->
            <template v-if="section.id === 'name'">
              <div class="title"> {{optionProperties[section.index].name}} </div>

              <div :class="['content name', {'with-tags': enableTags}]">
                <div :class="['editable', {'show': optionProperties[section.index].editable}]">
                  <el-input v-model="templateEdit.name"
                    :ref="'styleInputName'"
                    @change="handlePropertyChange(optionProperties[section.index].id)"
                    class="cstm-input-grey">
                  </el-input>
                </div>
                <div :class="['text-label', {'show': !optionProperties[section.index].editable}]" @click="handlePropertyClick(optionProperties[section.index].id)">{{templateEdit.name}}</div>
              </div>
            </template>
          </div>
        </template>

        <!-- Submit Button -->
        <el-button v-if="showSubmitButton" class="cstm-btn-gradient width-125 submit-btn" @click="handleSubmitButtonClick">{{ $t('label.ok') }}</el-button>
      </div>

      <!-- Media List Dialog -->
      <div class="cstm-modal-media-list">
        <el-dialog
          v-if="mediaListDialog.isVisible"
          id="mediaDialogInCreateProject"
          :z-index="999"
          :model-value="mediaListDialog.isVisible"
          :close-on-click-modal="false"
          :class="{'cstm-modal-media-list': true, 'vertical': (projectOrientation === 'vertical')}"
          :modal="false"
          @close="handleDialogClose">

            <media-list
              :defaultMediaTypeFilterProp="mediaListDialog.filterType"
              :mediaSelectionProp="mediaListDialog.mediaSelection"
              :mediaTypeOptionsProp="mediaListDialog.filterTypeOptions"
              :isMediaListVisibleProp="mediaListDialog.isVisible"
              @media-list-change="handleMediaListChange($event)">
            </media-list>
        </el-dialog>
      </div>
    </div>
  </div>
</template>
<script src="./create-template-set-menu.js"></script>
<style lang="scss" src="./create-template-set-menu.scss"></style>
